import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CometChat } from '@cometchat-pro/chat';
import forEach from 'lodash/forEach';

import { activeChatIdSelector } from 'store/selectors/messagesSelectors';
import { isAuthenticatedUserSelector, userDataSelector, userSettingsSelector } from 'store/selectors/userSelectors';
import {
  getMessageAction,
  getUnreadCountMessagesAction,
  resetMessagesStoreAction,
  setChatStatusAction,
  setIsOnlineUserAction,
  setIsUserTypingAction,
  setMessageReadAction,
} from 'store/actions/messagesActions';
import { useTypedSelector } from 'hooks/useTypedSelector';
import Users from 'api/classes/Users';
import { getCometChatAuthTokenRequest } from 'api/requests/messagesRequests';
import { COMET_CHAT_APP_ID } from 'utils/constants/constants';
import { EAccountStatuses } from 'types/userTypes';

const ADD_CONNECTION_LISTENER = 'addConnectionListener';
const ADD_LOGIN_LISTENER = 'addLoginListener';
const ADD_MESSAGE_LISTENER = 'addMessageListener';
const ADD_USER_LISTENER = 'addUserListener';

type ICometChatListenersName = typeof ADD_CONNECTION_LISTENER
  | typeof ADD_LOGIN_LISTENER
  | typeof ADD_MESSAGE_LISTENER
  | typeof ADD_USER_LISTENER;

const cometChatListenersNames: ICometChatListenersName[] = [
  ADD_CONNECTION_LISTENER,
  ADD_LOGIN_LISTENER,
  ADD_MESSAGE_LISTENER,
  ADD_USER_LISTENER,
];

const appSetting: CometChat.AppSettings = new CometChat.AppSettingsBuilder()
  .subscribePresenceForAllUsers()
  .setRegion('us')
  .autoEstablishSocketConnection(true)
  .build();

export let chatListRequest: CometChat.ConversationsRequest;

export default function() {
  const dispatch = useDispatch();
  const isAuthenticated = useTypedSelector(isAuthenticatedUserSelector);
  const activeChatId = useTypedSelector(activeChatIdSelector);
  const { id } = useTypedSelector(userDataSelector);
  const { approvalStatus } = useTypedSelector(userSettingsSelector);

  const handeConversationsRequestBuilder = () => {
    chatListRequest = new CometChat.ConversationsRequestBuilder()
      .setConversationType('user')
      .setLimit(15)
      .build();
  };

  // Logs into CometChat using a short-lived auth token issued by the backend.
  // The backend creates the CometChat user if needed, so no full-access key is used client-side.
  const handleCometChatLogin = async() => {
    const { success, data } = await getCometChatAuthTokenRequest();

    if (success && data?.authToken) {
      await CometChat.login(data.authToken);
    }
  };

  const initChat = async() => {
    const isInit = await CometChat.init(COMET_CHAT_APP_ID, appSetting);

    handeConversationsRequestBuilder();
    if (isInit) {
      const loggedInUser = await CometChat.getLoggedinUser();

      if (loggedInUser) {
        dispatch(setChatStatusAction({ isLoggedInChat: true }));
      } else {
        await handleCometChatLogin();
      }

      dispatch(getUnreadCountMessagesAction());
    }
  };

  const cometChatListeners = {
    [ADD_CONNECTION_LISTENER]: new CometChat.ConnectionListener({
      onConnected: () => dispatch(setChatStatusAction({ isConnected: true })),
    }),
    [ADD_LOGIN_LISTENER]: new CometChat.LoginListener({
      loginSuccess: () => {
        handeConversationsRequestBuilder();
        dispatch(resetMessagesStoreAction());
        dispatch(setChatStatusAction({ isLoggedInChat: true }));
        dispatch(getUnreadCountMessagesAction());
        CometChat.removeLoginListener('loginListener');
      },
      loginFailure: async(error: CometChat.CometChatException) => {
        if (error.code === 'ERR_UID_NOT_FOUND') {
          // User creation is a privileged operation — delegate it to the backend,
          // then retry login with a fresh scoped token.
          const { success } = await Users.activateChat({ id });

          if (success) {
            await handleCometChatLogin();
          }
        }
      },
    }),
    [ADD_MESSAGE_LISTENER]: new CometChat.MessageListener({
      onTextMessageReceived: (message: CometChat.TextMessage) => {
        dispatch(getMessageAction(message));
      },
      onMediaMessageReceived: (mediaMessage: CometChat.MediaMessage) => {
        dispatch(getMessageAction(mediaMessage));
      },
      onTypingStarted: (typingIndicator: CometChat.TypingIndicator) => {
        if (typingIndicator?.getSender().getUid() === activeChatId) {
          dispatch(setIsUserTypingAction({ isTyping: true }));
        }
      },
      onTypingEnded: (typingIndicator: CometChat.TypingIndicator) => {
        if (typingIndicator?.getSender().getUid() === activeChatId) {
          dispatch(setIsUserTypingAction({ isTyping: false }));
        }
      },
      onMessagesRead: (messageReceipt: CometChat.MessageReceipt) => {
        dispatch(setMessageReadAction(messageReceipt));
      },
    }),
    [ADD_USER_LISTENER]: new CometChat.UserListener({
      onUserOnline: (onlineUser: CometChat.User) => {
        dispatch(setIsOnlineUserAction(onlineUser));
      },
      onUserOffline: (offlineUser: CometChat.User) => {
        dispatch(setIsOnlineUserAction(offlineUser));
      },
    }),
  };

  forEach(cometChatListenersNames, (listenerName) =>
    CometChat[listenerName](listenerName, cometChatListeners[listenerName]));

  useEffect(() => {
    if (isAuthenticated && approvalStatus === EAccountStatuses.Approved) {
      initChat();
    }
  }, [isAuthenticated, approvalStatus]);
}
